import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@tanstack/react-pacer';
import { Button, Popover, PopoverTrigger, PopoverSurface, SearchBox, ToggleButton, Tooltip, makeStyles } from '@fluentui/react-components';
import type { SearchBoxChangeEvent } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { SearchScope } from '../../../../api/generated/v2/generated/data-contracts';
import { useInitialData } from '../../../../hooks/useInitialData';
import { useIsGridReadOnly } from '../../state';
import { useTaskSearch } from './useTaskSearch';
import { TaskSearchResults } from './TaskSearchResults';

type TaskSearchProps = {
    externalOpen?: boolean;
    externalQuery?: string;
    onOpenChange?: (open: boolean) => void;
    onScrollToTask?: (taskId: string) => void;
};

type ScopeOption = {
    label: string;
    value: SearchScope;
};

const useStyles = makeStyles({
    surface: {
        width: '400px',
        maxWidth: '90vw',
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '12px',
        paddingRight: '12px',
    },
    searchBox: {
        width: '100%',
        marginBottom: '8px',
    },
    scopeBar: {
        display: 'flex',
        columnGap: '4px',
        marginBottom: '8px',
    },
    scopeButton: {
        minWidth: 'auto',
        fontSize: '12px',
    },
});

export const TaskSearch = memo(function TaskSearch({ externalOpen, externalQuery, onOpenChange, onScrollToTask }: TaskSearchProps) {
    const { t } = useTranslation('timesheet');
    const styles = useStyles();
    const isReadOnly = useIsGridReadOnly();
    const {
        data: {
            configuration: { onlyAssignedSearch },
        },
    } = useInitialData();

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [scope, setScope] = useState<SearchScope>('AssignedTasks');

    const [debouncedQuery] = useDebouncedValue(inputValue, { wait: 300 });

    const effectiveScope = onlyAssignedSearch ? 'AssignedTasks' : scope;
    const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useTaskSearch(debouncedQuery, effectiveScope);

    const scopeOptions: ScopeOption[] = useMemo(
        () => [
            { label: t('search.cta.me'), value: 'AssignedTasks' },
            { label: t('search.cta.project'), value: 'AssignedProjects' },
            { label: t('task.column.all'), value: 'All' },
        ],
        [t],
    );

    // Handle external open control
    useEffect(() => {
        if (externalOpen !== undefined) {
            setOpen(externalOpen);
        }
    }, [externalOpen]);

    // Handle external query pre-fill
    useEffect(() => {
        if (externalQuery !== undefined && externalQuery !== inputValue) {
            setInputValue(externalQuery);
        }
    }, [externalQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenChange = useCallback(
        (_e: unknown, data: { open: boolean }) => {
            setOpen(data.open);
            onOpenChange?.(data.open);
            if (!data.open) {
                // Reset on close
                setInputValue('');
            }
        },
        [onOpenChange],
    );

    const handleInputChange = useCallback((_e: SearchBoxChangeEvent, data: { value: string }) => {
        setInputValue(data.value);
    }, []);

    const handleTaskAdded = useCallback(
        (taskId: string) => {
            onScrollToTask?.(taskId);
            setOpen(false);
            onOpenChange?.(false);
        },
        [onScrollToTask, onOpenChange],
    );

    const handleScopeChange = useCallback((newScope: SearchScope) => {
        setScope(newScope);
    }, []);

    const triggerButton = (
        <Button appearance="subtle" icon={<SearchRegular />} disabled={isReadOnly}>
            {t('cta.search')}
        </Button>
    );

    return (
        <Popover open={open} onOpenChange={handleOpenChange} positioning="below-start" trapFocus>
            <PopoverTrigger disableButtonEnhancement>
                {isReadOnly ? (
                    <Tooltip content={t('tooltip.searchDisabled')} relationship="description">
                        {triggerButton}
                    </Tooltip>
                ) : (
                    triggerButton
                )}
            </PopoverTrigger>
            <PopoverSurface className={styles.surface}>
                <SearchBox className={styles.searchBox} value={inputValue} onChange={handleInputChange} placeholder={t('search.searchPlaceholder')} autoFocus />
                {!onlyAssignedSearch && (
                    <div className={styles.scopeBar}>
                        {scopeOptions.map(option => (
                            <ToggleButton
                                key={option.value}
                                className={styles.scopeButton}
                                appearance="subtle"
                                size="small"
                                checked={scope === option.value}
                                onClick={() => handleScopeChange(option.value)}
                            >
                                {option.label}
                            </ToggleButton>
                        ))}
                    </div>
                )}
                <TaskSearchResults
                    data={data?.pages}
                    isLoading={isLoading}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    scope={effectiveScope}
                    onFetchNextPage={fetchNextPage}
                    onTaskAdded={handleTaskAdded}
                />
            </PopoverSurface>
        </Popover>
    );
});
